import type {ComponentContent} from '@/ts/types/dto/interventions-dto';
import type {ContentTileDTO} from '@/ts/types/dto/today-dto';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import type {TodayContent} from '@/ts/types/component/today-page';
import {contentMediaType} from '@/ts/utils/recco-label-keys';
import {withConsumed} from '@/ts/utils/consumed-overlay';

/**
 * Translates the content tile's slice of `GET /me/today` into what the two cards render.
 *
 * Nothing is chosen here. Which pieces land in the pair, how they are ranked and when they rotate
 * is entirely the backend's (IPD-2969), and the payload arrives already resolved — so this is a
 * field-for-field translation with two coercions and one derivation, and no logic worth hiding.
 */

/**
 * The medium is not a field on `ComponentContent`: it is read off which media the piece carries.
 * Explore's cards already do that, and this is the same derivation they use rather than a second
 * copy — the play badge, the Watched / Read / Listened wording and Explore's own labels all key
 * off the enum it returns, so a parallel one here would be free to drift from theirs.
 */
function toTodayContent(item: ComponentContent, hasViewed: HasViewed): TodayContent {
  // The backend's flag alone is one refresh behind: it only turns true server-side, so a piece
  // opened and come back from still reads unread until the page is fetched again. That is the bug
  // IPD-3035 fixed for every other card surface, and it fixed it with this same overlay rather
  // than per family, so the marker cannot mean different things in two places. Monotonic: it only
  // ever turns the flag on.
  const seen = withConsumed(item, hasViewed(item.id));

  return {
    id: item.id,
    // `name` is nullable on the DTO (a `no_text` block has none) while the card takes a string.
    // An empty title is drawn as an empty line rather than the word "null", and the card already
    // drops blank parts when it builds its accessible name.
    title: item.name ?? '',
    media: contentMediaType(item),
    // `?? null` because the field is optional on the DTO as well as nullable, and the card's prop
    // is declared `string | null`.
    imageUrl: item.image ?? null,
    duration: item.duration ?? null,
    companyImage: item.companyImage,
    // Optional on the FE type only: the schema has it in `ComponentContent.required`, so a payload
    // without it is a contract break rather than "unknown", and unread is the safe reading of one.
    read: seen.consumed ?? false,
    // `state` names the back control on the page this opens: without it `back-link` falls back to
    // a generic "Back" instead of the page the user actually came from. Every other route into
    // block-content passes it (Explore's cards, the home band, the sidebar bookmarks).
    to: {name: 'block-content', params: {itemId: item.id}, state: {backLabel: 'home'}},
  };
}

/**
 * The pair the page reads from, in the order the backend fixed it.
 *
 * The array is returned at whatever length it arrives. Two is the normal case, but one is a real
 * one the backend produces on purpose: when a pinned piece disappears from Directus mid-window,
 * the survivor stays alone until the pair next refreshes. Padding that back to two would invent a
 * card, so the count travels as it is and the grid drops the cell it no longer fills.
 *
 * `hasViewed` stays a parameter rather than a store read so this remains pure; the wiring ticket
 * passes `useViewedContentStore`'s own predicate. Defaulting it to "nothing seen yet" keeps the
 * backend's flag as the only source when a caller has no session state to add.
 *
 * CALL THIS FROM INSIDE A `computed`. The overlay is the whole reason a piece opened during this
 * session shows its marker on the way back, and that only works if reading `hasViewed` registers
 * the store's set as a dependency. Both surfaces the IPD-3035 fix shipped on do exactly that
 * (`block-content-band.vue`, `explore-library-rail.vue`). Assigning the result once in a fetch
 * handler compiles, passes every test below, and quietly restores the bug this exists to prevent.
 */
export function mapTodayContent(dto: ContentTileDTO, hasViewed: HasViewed = () => false): TodayContent[] {
  return dto.items.map((item) => toTodayContent(item, hasViewed));
}

/**
 * Whether the piece was opened during this session, which the backend cannot know yet.
 *
 * Takes the id at the type the content carries it (`Content.id` is a `UuidDTO`) rather than a bare
 * string, so the predicate the wiring passes — `useViewedContentStore`'s own `hasViewed` — fits
 * without a cast at the call site.
 */
export type HasViewed = (contentId: UuidDTO) => boolean;
